// src/components/separation/checklistslist.tsx
// Exit-checklist list — replaces the old "Select a resignation…" placeholder.

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import { ChecklistListRow } from '../../types/separation';
import { getChecklists } from '../../api/separation';
import { statusBadge, employeeLabel, fmtDate } from './uiHelpers';

interface ChecklistsListProps {
  userRole?: string;
  onOpen?: (separationType: string, separationId: string) => void;
}

const ChecklistsList: React.FC<ChecklistsListProps> = ({ onOpen }) => {
  const [rows, setRows] = useState<ChecklistListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getChecklists({ page, limit });
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Failed to load checklists:', e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.ceil(total / limit) || 1;

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Exit Checklists</h3>
        <p className="text-xs text-gray-500 mt-0.5">Total: {total}</p>
      </div>

      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-500">
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Employee</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Separation</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Progress</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Status</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Created</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((c) => {
              const badge = statusBadge(c.status);
              const pct = c.total_items ? Math.round((c.completed_items / c.total_items) * 100) : 0;
              return (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{employeeLabel(c)}</div>
                    <div className="text-xs text-gray-400">{c.employee_code || `#${c.id.slice(0, 8)}`}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">{(c.separation_type || '').toLowerCase()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {c.completed_items}/{c.total_items}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{fmtDate(c.created_at)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => onOpen?.(c.separation_type, c.separation_id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  <ListChecks className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  No exit checklists yet. They are created automatically when a resignation is
                  finalised or an employee is terminated.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gt-btn is-ghost is-sm disabled:opacity-50">
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="gt-btn is-ghost is-sm disabled:opacity-50">
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChecklistsList;
